// src/App.js
import React, { useEffect, useState } from "react";
import Home from "./Pages/Home";
import { TokenProvider, useToken } from "./components/Firebase/TokenContext";

const AppContent = () => {
  const { firebaseToken, setFirebaseToken } = useToken();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Authentication + session restoration
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get("data");

    // 1️⃣ Handle redirect after login from auth.s.com
    if (dataParam) {
      try {
        const decoded = JSON.parse(atob(dataParam));
        if (decoded.token && decoded.email) {
          // Save to state
          setFirebaseToken(decoded.token);
          setUser({ email: decoded.email });

          // ✅ Session only (clears when browser closes)
          sessionStorage.setItem("firebaseToken", decoded.token);
          sessionStorage.setItem("user", JSON.stringify({ email: decoded.email }));

          // ✅ Cookie for multi-tab access
          document.cookie = `firebaseToken=${decoded.token}; path=/; SameSite=Lax`;
          document.cookie = `user=${encodeURIComponent(JSON.stringify({ email: decoded.email }))}; path=/; SameSite=Lax`;

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error("Error decoding login data:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // 2️⃣ Restore from sessionStorage (after refresh)
    const storedToken = sessionStorage.getItem("firebaseToken");
    const storedUserJSON = sessionStorage.getItem("user");
    if (storedToken && storedUserJSON) {
      const storedUser = JSON.parse(storedUserJSON);
      setFirebaseToken(storedToken);
      setUser(storedUser);
      setLoading(false);
      return;
    }

    // 3️⃣ Restore from cookies (multi-tab)
    const tokenMatch = document.cookie.match(/(?:^|;\s*)firebaseToken=([^;]+)/);
    const userMatch = document.cookie.match(/(?:^|;\s*)user=([^;]+)/);

    if (tokenMatch && userMatch) {
      const cookieToken = decodeURIComponent(tokenMatch[1]);
      const cookieUser = JSON.parse(decodeURIComponent(userMatch[1]));

      setFirebaseToken(cookieToken);
      setUser(cookieUser);

      // Sync back to sessionStorage
      sessionStorage.setItem("firebaseToken", cookieToken);
      sessionStorage.setItem("user", JSON.stringify(cookieUser));

      setLoading(false);
      return;
    }

    // 4️⃣ No session → redirect to login
    const currentUrl = window.location.origin;
    const authURL = `https://auth.yesedacademy.com?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = authURL;
  }, [setFirebaseToken]);

  // 🔹 Heartbeat system for detecting browser close
  useEffect(() => {
    const LOGOUT_TIMEOUT = 10000; // 10 seconds
    const HEARTBEAT_KEY = "browser_heartbeat";

    // Update heartbeat every 5 seconds
    const heartbeatInterval = setInterval(() => {
      localStorage.setItem(HEARTBEAT_KEY, Date.now().toString());
    }, 5000);

    // Check heartbeat (detect full browser close)
    const monitorInterval = setInterval(() => {
      const last = parseInt(localStorage.getItem(HEARTBEAT_KEY) || "0", 10);
      if (Date.now() - last > LOGOUT_TIMEOUT) {
        console.log("🧹 Browser closed → Logging out session");
        handleLogout();
      }
    }, 7000);

    // Sync logout across tabs
    const handleStorage = (e) => {
      if (e.key === "cross_tab_logout") {
        console.log("🔄 Detected logout in another tab");
        handleLogout();
      }
    };
    window.addEventListener("storage", handleStorage);

    const handleLogout = () => {
      sessionStorage.clear();
      localStorage.removeItem(HEARTBEAT_KEY);
      localStorage.setItem("cross_tab_logout", Date.now().toString());
      localStorage.removeItem("cross_tab_logout");
      setFirebaseToken(null);
      setUser(null);
    };

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(monitorInterval);
      window.removeEventListener("storage", handleStorage);
    };
  }, [setFirebaseToken]);

  if (loading) return <div>Loading authentication...</div>;

  return firebaseToken && user ? <Home user={user} /> : null;
};

const App = () => (
  <TokenProvider>
    <AppContent />
  </TokenProvider>
);

export default App;
